import { classMerge } from "../utility/classMerge";
import { useScaffold } from "./Scaffold";
import styles from "./Scaffold.module.css";
import { ScaffoldRow } from "./ScaffoldRow";

/**
 * Component to render groups of scaffold rows based on their row group number, applying appropriate styles for headers, body, and footers.
 *
 * This component uses the ScaffoldContext to access row configurations and renders rows grouped by their rowGroupNumber, applying styles based on their type (header, body, footer).
 *
 * @returns A React element representing the grouped scaffold rows.
 * @throws Will throw an error if used outside of a ScaffoldContext provider.
 */
export const ScaffoldRowGroups = () => {
  const scaffoldContext = useScaffold();
  const currentRowIdSet = new Set(scaffoldContext.settings.rows.map((r) => r.id));

  const rgStyles = {
    header: styles.header,
    body: styles.body,
    footer: styles.footer,
  };

  const allDisplayRows = [
    ...scaffoldContext.settings.rows,
    ...scaffoldContext.state.leavingRows,
  ].sort((a, b) => {
    if (a.rowNumber !== b.rowNumber) {
      return a.rowNumber - b.rowNumber;
    }

    // When row numbers collide during removal, keep the leaving row first so it visibly collapses.
    const isALeavingRow = !currentRowIdSet.has(a.id);
    const isBLeavingRow = !currentRowIdSet.has(b.id);
    if (isALeavingRow === isBLeavingRow) {
      return 0;
    }

    return isALeavingRow ? -1 : 1;
  });

  const rowGroupList = new Set(allDisplayRows.map((r) => r.rowGroupNumber));
  const rowGroupTypes = allDisplayRows.reduce(
    (acc, r) => {
      acc[r.rowGroupNumber] = r.type;
      return acc;
    },
    {} as Record<number, "header" | "body" | "footer">,
  );

  return Array.from(rowGroupList).map((rgNum) => (
    <div
      key={`row-group-${rgNum}`}
      className={classMerge([styles.rowGroup, rgStyles[rowGroupTypes[rgNum]]])}
    >
      {allDisplayRows
        .filter((r) => r.rowGroupNumber === rgNum)
        .map((r, ix, arr) => {
          let id = r.id;
          const style: React.CSSProperties = {};
          /* Check for non-unique rowIDs from bad input data and highlight them,
             added for debugging, doesn't require testing */
          // istanbul ignore next
          if (arr.findIndex((row) => row.id === r.id) !== ix) {
            console.warn(`Duplicate row ID detected: ${r.id}. Appending index to make unique.`);
            id = `${r.id}-${ix}`;
            style.background = "rgba(255, 0, 0, 0.1)";
          }
          return (
            <ScaffoldRow
              key={`row-${id}`}
              rowId={r.id}
              style={style}
            />
          );
        })}
    </div>
  ));
};

ScaffoldRowGroups.displayName = "ScaffoldRowGroups";
